import React, { useState, useEffect } from "react";
import { Search, BookOpen, Bookmark, Menu, X, Sun, Moon } from "lucide-react";

const NAV = [
  { id: "about", label: "Profile" },
  { id: "themes", label: "Themes" },
  { id: "spotlight", label: "Spotlight" },
  { id: "index", label: "The Index" },
  { id: "subscribe", label: "Subscribe" },
];

export default function Header({ onSearchClick, onSubscribe, onReadingList, savedCount = 0 }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState(() => document.documentElement.dataset.theme || "dark");

  const toggleMode = () => {
    const next = mode === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    localStorage.setItem("dl-theme", next);
    setMode(next);
    window.dispatchEvent(new CustomEvent("dl-theme", { detail: next }));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    });
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled || menuOpen ? "rgba(var(--ink-rgb),0.92)" : "rgba(var(--ink-rgb),0.4)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${scrolled || menuOpen ? "var(--line)" : "transparent"}`,
      }}
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <button
          data-testid="brand-home"
          onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center w-9 h-9 border font-serif text-lg font-semibold"
            style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>DL</span>
          <span className="hidden sm:flex flex-col items-start leading-none">
            <span className="font-serif text-[1.15rem] tracking-tight text-[var(--txt)]">Dre Lapiello</span>
            <span className="meta-label text-[0.56rem] text-[var(--muted)] mt-1">Intelligence Dossier</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)}
              className="meta-label text-[var(--muted)] hover:text-[var(--gold)] transition-colors">
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <button data-testid="theme-mode-toggle" onClick={toggleMode} aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="grid place-items-center w-9 h-9 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
            {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button data-testid="header-search-btn" onClick={onSearchClick} aria-label="Search"
            className="grid place-items-center w-9 h-9 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
            <Search size={16} />
          </button>
          <button data-testid="header-reading-list-btn" onClick={onReadingList} aria-label="Reading list"
            className="relative grid place-items-center w-9 h-9 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
            <Bookmark size={16} />
            {savedCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 grid place-items-center text-[0.6rem] font-mono font-semibold"
                style={{ backgroundColor: "var(--gold)", color: "var(--ink)" }}>{savedCount}</span>
            )}
          </button>
          <button data-testid="header-subscribe-btn" onClick={onSubscribe}
            className="hidden sm:flex items-center gap-2 h-9 px-4 border meta-label transition-all hover:gap-3"
            style={{ borderColor: "var(--gold)", color: "var(--ink)", backgroundColor: "var(--gold)" }}>
            <BookOpen size={14} /> Subscribe
          </button>
          <button data-testid="menu-toggle" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu"
            className="md:hidden grid place-items-center w-9 h-9 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
            {menuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav data-testid="mobile-menu" className="md:hidden overflow-hidden border-t border-[var(--line)]">
          <div className="px-5 py-3 flex flex-col">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => go(n.id)}
                className="text-left py-3 border-b border-[var(--line)] last:border-0 font-serif text-xl text-[var(--txt)] hover:text-[var(--gold)] transition-colors">
                {n.label}
              </button>
            ))}
            <button data-testid="mobile-nav-reading" onClick={() => { setMenuOpen(false); onReadingList(); }}
              className="text-left py-3 font-serif text-xl text-[var(--txt)] hover:text-[var(--gold)] transition-colors">
              Reading List {savedCount > 0 && <span className="meta-label text-[0.6rem] text-[var(--gold)]">· {savedCount}</span>}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
